// tokenFmt — human-first rendering of on-chain base-unit amounts.
//
// The crypto rail's wire format is raw integer base-unit strings (wei-
// style, up to 78 digits), which operators shouldn't have to eyeball.
// These helpers turn them into "0.02 tKUB"-style figures; the raw wire
// value stays available on hover via <TechHint>.

/** BigInt-safe base-units → decimal token amount, e.g. ("20000000000000000", 18) → "0.02". */
export function formatTokenBase(
  base: string | null | undefined,
  decimals: number,
): string | null {
  if (base === null || base === undefined) return null;
  const s = base.trim();
  if (!/^\d+$/.test(s)) return null; // not a plain base-unit integer
  let n: bigint;
  try {
    n = BigInt(s);
  } catch {
    return null;
  }
  if (decimals <= 0) return groupInt(n.toString());
  const div = 10n ** BigInt(decimals);
  const int = n / div;
  const frac = (n % div).toString().padStart(decimals, "0");
  // Keep at most 6 fraction digits, but never round a non-zero amount
  // down to nothing: extend until the first significant digit.
  let cut = frac.slice(0, 6);
  if (int === 0n && n !== 0n && !/[1-9]/.test(cut)) {
    const first = frac.search(/[1-9]/);
    cut = frac.slice(0, first + 1);
  }
  cut = cut.replace(/0+$/, "");
  return cut ? `${groupInt(int.toString())}.${cut}` : groupInt(int.toString());
}

/** "1234567" → "1,234,567". */
function groupInt(s: string): string {
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** formatTokenBase + symbol, with a fallback to the raw string. */
export function formatTokenAmount(
  base: string | null | undefined,
  decimals: number | undefined,
  symbol: string | undefined,
): string {
  const human = decimals === undefined ? null : formatTokenBase(base, decimals);
  if (human === null) return base || "—";
  return symbol ? `${human} ${symbol}` : human;
}

/* ── ⚡ ↔ token (SM-351) ───────────────────────────────────────────────
 *
 * A payout is REQUESTED in ⚡ and SENT in tokens; the rail converts at
 * the asset's active rate when it prices the order. These helpers do the
 * same arithmetic the rail does, in BigInt, so a screen can say what an
 * amount is worth before the rail has spoken — and say it in the same
 * numbers the rail will later produce.
 *
 * Both directions FLOOR, matching crypto-wallet-service: a preview must
 * never promise more than the send will actually carry.
 */

/** The rate shape carried by /admin/me/withdrawal-options. */
export interface TokenRateLike {
  rate_num?: number;
  rate_den?: number;
  spark_minor_per_unit?: number;
}

/** The fee half of a withdrawal policy, same wire shape. */
export interface WithdrawalFeeLike {
  fee_mode?: string;
  fee_value?: number;
}

function big(v: number | string | undefined | null): bigint | null {
  if (v === undefined || v === null || v === "") return null;
  try {
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
    return BigInt(n);
  } catch {
    return null;
  }
}

/**
 * ⚡ (public 4dp minor units) → gross token amount in base units:
 *   floor(spark_minor × 10^decimals × rate_den / (rate_num × spark_minor_per_unit))
 * Null when the asset carries no usable rate — the caller shows nothing
 * rather than a conversion nobody has priced.
 */
export function tokenBaseFromSparkMinor(
  sparkMinor: string | number | bigint | undefined | null,
  rate: TokenRateLike | undefined,
  decimals: number | undefined,
): bigint | null {
  if (!rate || decimals === undefined || !Number.isInteger(decimals) || decimals < 0) {
    return null;
  }
  let minor: bigint | null;
  try {
    minor = typeof sparkMinor === "bigint" ? sparkMinor : big(sparkMinor as number | string);
  } catch {
    return null;
  }
  const num = big(rate.rate_num);
  const den = big(rate.rate_den);
  const perUnit = big(rate.spark_minor_per_unit);
  if (minor === null || num === null || den === null || perUnit === null) return null;
  if (minor < 0n || num <= 0n || den <= 0n || perUnit <= 0n) return null;
  return (minor * 10n ** BigInt(decimals) * den) / (num * perUnit);
}

/**
 * The inverse of tokenBaseFromSparkMinor: token base units → ⚡ (public
 * 4dp minor units):
 *   floor(base × rate_num × spark_minor_per_unit / (10^decimals × rate_den))
 *
 * Used where the operator types a TOKEN amount and wants to know what it
 * is worth in ⚡ — the system-account on-chain withdrawal form, where the
 * send is denominated in tokens but the ledger hold is in ⚡ (SM-335).
 * Floors for the same reason as the forward direction: a preview must
 * never promise more than the rail will actually move.
 */
export function sparkMinorFromTokenBase(
  base: string | bigint | undefined | null,
  rate: TokenRateLike | undefined,
  decimals: number | undefined,
): bigint | null {
  if (!rate || decimals === undefined || !Number.isInteger(decimals) || decimals < 0) {
    return null;
  }
  let units: bigint;
  try {
    if (typeof base === "bigint") units = base;
    else if (typeof base === "string" && /^\d+$/.test(base.trim())) units = BigInt(base.trim());
    else return null;
  } catch {
    return null;
  }
  const num = big(rate.rate_num);
  const den = big(rate.rate_den);
  const perUnit = big(rate.spark_minor_per_unit);
  if (num === null || den === null || perUnit === null) return null;
  if (units < 0n || num <= 0n || den <= 0n || perUnit <= 0n) return null;
  return (units * num * perUnit) / (10n ** BigInt(decimals) * den);
}

/**
 * The rail's withdrawal fee on a gross token amount, in base units:
 * FLAT takes fee_value base units, PCT takes fee_value basis points,
 * GAS_PASSTHROUGH is free at request time (the platform pays gas).
 * Mirrors withdrawalFee() in crypto-wallet-service.
 */
export function withdrawalFeeBase(
  grossBase: bigint | null,
  fee: WithdrawalFeeLike | undefined,
): bigint {
  if (grossBase === null || grossBase <= 0n || !fee) return 0n;
  const value = big(fee.fee_value) ?? 0n;
  if (value <= 0n) return 0n;
  switch (fee.fee_mode) {
    case "PCT":
      return (grossBase * value) / 10_000n;
    case "GAS_PASSTHROUGH":
      return 0n;
    default: // FLAT
      return value;
  }
}

/**
 * BigInt-safe decimal → base-units string, e.g. ("0.02", 18) →
 * "20000000000000000". Null when the text isn't a valid amount or has
 * more fraction digits than the asset supports.
 */
export function parseTokenToBase(
  text: string,
  decimals: number,
): string | null {
  const s = text.trim().replace(/,/g, "");
  const m = /^(\d*)(?:\.(\d+))?$/.exec(s);
  if (!m || (!m[1] && !m[2])) return null;
  const int = m[1] || "0";
  const frac = m[2] ?? "";
  if (frac.length > decimals) return null;
  const n =
    BigInt(int) * 10n ** BigInt(decimals) +
    BigInt(frac.padEnd(decimals, "0") || "0");
  return n.toString();
}
